import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
// The page's CDN copy of the icons predates the WhatsApp icon.
import "bootstrap-icons/font/bootstrap-icons.css";
import { getPublicEmergencyContacts, telLink, whatsappLink } from "../services/emergencyContactApi";

const RETRY_SECONDS = 60;

/**
 * Who to call in an emergency, at the top of the customer trip page. The server sends only the
 * people on duty now and says when that next changes, so the card reloads itself then - a page
 * left open overnight swaps the day contact for the night one without a refresh.
 */
export default function TrackingEmergencyContacts({ token, onShownChange }) {
  const [data, setData] = useState(null);
  const shown = Boolean(data?.shown);

  // The trip map's Emergency button only appears while there is someone here to call.
  useEffect(() => {
    onShownChange?.(shown);
  }, [shown, onShownChange]);

  useEffect(() => {
    if (!token) return undefined;
    let timer;
    let cancelled = false;
    const load = async () => {
      let wait = RETRY_SECONDS;
      try {
        const result = await getPublicEmergencyContacts(token);
        if (cancelled) return;
        setData(result);
        wait = Number(result?.refreshInSeconds) || RETRY_SECONDS;
      } catch (error) {
        // Keep what is already shown; a dropped connection on a hill road should not blank it.
      }
      if (!cancelled) timer = setTimeout(load, wait * 1000);
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token]);

  if (!data?.shown) return null;
  const contacts = data.contacts || [];

  return (
    <div className="public-tracking-emergency" data-tour="emergency">
      <div className="public-tracking-emergency-title">
        <i className="bi bi-telephone-inbound-fill" aria-hidden="true" />
        Emergency Contact
      </div>
      {contacts.length ? contacts.map((contact) => (
        <div className="public-tracking-emergency-contact" key={`${contact.name}-${contact.phone}`}>
          <div>
            <div className="public-tracking-emergency-name">{contact.name}</div>
            <div className="public-tracking-emergency-meta">
              {[contact.role, contact.languages ? `Speaks ${contact.languages}` : ""].filter(Boolean).join(" · ")}
            </div>
            <div className="public-tracking-emergency-meta">{contact.phone}</div>
          </div>
          <div className="public-tracking-emergency-actions">
            <Button as="a" href={telLink(contact.phone)} variant="danger">
              <i className="bi bi-telephone-fill me-2" aria-hidden="true" />Call
            </Button>
            {contact.whatsapp && whatsappLink(contact.phone) ? (
              <Button as="a" href={whatsappLink(contact.phone)} target="_blank" rel="noopener noreferrer" variant="success">
                <i className="bi bi-whatsapp me-2" aria-hidden="true" />WhatsApp
              </Button>
            ) : null}
          </div>
        </div>
      )) : (
        <div className="public-tracking-emergency-wait">
          {data.nextAvailableLabel
            ? `Emergency contact available from ${data.nextAvailableLabel}`
            : "No emergency contact is available right now."}
        </div>
      )}
    </div>
  );
}
